// domain/services/chargeMasterResolution.js
import { isChargeMinorUnits } from '../constants/currency';

/**
 * What a clinic charges for a code, read off a sheet that has already been resolved for a date.
 *
 * PURE, AND TAKES AN ALREADY-RESOLVED SHEET. Picking which sheet applies to a service date is
 * `VersionTimeline`'s job and happens one layer up; everything here asks a closed question of one
 * sheet. Keeping the two apart is what stops a second, subtly different resolver growing here — the
 * failure the service-code and funding-source resolvers were each careful to avoid.
 *
 * A NULL SHEET IS AN ANSWER. It means no charge master was in force on the date being asked about,
 * so nothing is charged and every item holds. It is never a reason to reach for a different sheet:
 * falling forward to whatever the clinic charges NOW would bill a February service at a March
 * charge, silently, with nothing on the record to show it happened.
 *
 * ON THE SHEET AND CHARGED ARE DIFFERENT QUESTIONS, and conflating them is the mistake this module
 * exists to prevent. A line with no charge yet is on the sheet — the clinic has decided to bill this
 * code — and holds at billing because nobody has said for how much. The picker needs the first
 * question so it can stop a code being added twice; billing needs the second.
 */

/**
 * The line for a code on this sheet, or null.
 *
 * REFUSES TO ARBITRATE BETWEEN DUPLICATES rather than picking one. One line per code is an invariant
 * the write path holds, not something an array can enforce structurally, so a mis-seeded or
 * hand-edited sheet could carry two. Choosing between two charges for one code is exactly the guess
 * this context does not make: the honest outcome is the same visible hold as an absent line, and the
 * data is where it gets fixed. Same reasoning as `findByCodeOn` in the service-code resolver.
 *
 * @param {import('../model/ChargeMaster.types').ChargeMaster|null|undefined} chargeMaster
 * @param {string|null|undefined} serviceCodeId
 * @returns {import('../model/ChargeLine.types').ChargeLine|null}
 */
export function lineFor(chargeMaster, serviceCodeId) {
  if (chargeMaster === null || chargeMaster === undefined) {
    return null;
  }
  if (typeof serviceCodeId !== 'string' || serviceCodeId.length === 0) {
    return null;
  }
  const matches = (chargeMaster.lines ?? [])
    .filter((line) => line?.serviceCodeId === serviceCodeId);

  return matches.length === 1 ? matches[0] : null;
} // lineFor()

/**
 * Is this code on the sheet at all, charged or not?
 *
 * WHAT THE PICKER ASKS. A code already on the sheet is shown and unpickable rather than hidden, so
 * somebody looking for it finds it and learns why they cannot add it again.
 *
 * ASKS MEMBERSHIP DIRECTLY RATHER THAN THROUGH `lineFor`, because the two questions disagree on a
 * malformed sheet and this is the answer that has to stay right. `lineFor` refuses to arbitrate
 * between duplicate lines, and reading that refusal as "absent" would let the picker offer a code the
 * sheet already carries twice — adding a third copy to a sheet whose problem is that it has two.
 *
 * @param {import('../model/ChargeMaster.types').ChargeMaster|null|undefined} chargeMaster
 * @param {string|null|undefined} serviceCodeId
 * @returns {boolean}
 */
export function isOnSheet(chargeMaster, serviceCodeId) {
  if (typeof serviceCodeId !== 'string' || serviceCodeId.length === 0) {
    return false;
  }
  return (chargeMaster?.lines ?? []).some((line) => line?.serviceCodeId === serviceCodeId);
} // isOnSheet()

/**
 * The charge for a code, in minor units of the sheet's currency, or null.
 *
 * NULL COVERS THREE DIFFERENT SITUATIONS and deliberately does not distinguish them: no sheet in
 * force, the code not on the sheet, and the code on the sheet with no charge set. All three mean the
 * same thing to billing — there is no charge to put on a claim, so the item holds — and a caller
 * that needed to tell them apart to decide whether to bill would be deciding something it should
 * not. The screens that DO need to tell them apart ask the narrower questions above, where the
 * difference is about what to show a human rather than what to bill.
 *
 * @param {import('../model/ChargeMaster.types').ChargeMaster|null|undefined} chargeMaster
 * @param {string|null|undefined} serviceCodeId
 * @returns {number|null}
 */
export function chargeFor(chargeMaster, serviceCodeId) {
  const line = lineFor(chargeMaster, serviceCodeId);

  return isChargeMinorUnits(line?.chargeMinor) ? line.chargeMinor : null;
} // chargeFor()

/**
 * Does this code have a charge on this sheet?
 *
 * WHAT THE CONFIG-TIME GUARD ASKS, when a treatment service or a billing rule references a code. A
 * false answer is a warning and never a block: a clinic mid-setup, with a code referenced and not
 * yet charged, has made no mistake, and refusing the save would turn an ordinary intermediate state
 * into a failure.
 *
 * ZERO IS A CHARGE. A line charged at zero says the clinic bills this code at no cost, which is a
 * decision somebody made; a line with no charge says nobody has decided. Treating zero as absent
 * would delete the difference.
 *
 * @param {import('../model/ChargeMaster.types').ChargeMaster|null|undefined} chargeMaster
 * @param {string|null|undefined} serviceCodeId
 * @returns {boolean}
 */
export function hasCharge(chargeMaster, serviceCodeId) {
  return chargeFor(chargeMaster, serviceCodeId) !== null;
} // hasCharge()

/**
 * The codes on this sheet that carry no charge, in the order the sheet lists them.
 *
 * WHAT THE SAVE WARNING NAMES. The clinic is told which codes will hold at billing and saves anyway
 * if it wants to; the list exists so the consequence is visible at the moment of the decision rather
 * than discovered later on a claim. Deliberately NOT a count, and never a proportion — a clinic
 * charges for a small subset of the catalog by design, so there is no denominator and any score
 * built from one would be measuring nothing.
 *
 * @param {import('../model/ChargeMaster.types').ChargeMaster|null|undefined} chargeMaster
 * @returns {string[]} service code ids
 */
export function unchargedCodeIds(chargeMaster) {
  return (chargeMaster?.lines ?? [])
    .filter((line) => !isChargeMinorUnits(line?.chargeMinor))
    .map((line) => line?.serviceCodeId)
    .filter((serviceCodeId) => typeof serviceCodeId === 'string' && serviceCodeId.length > 0);
} // unchargedCodeIds()
